import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { ArtInfo } from "./useInfo";
import { parseAotdFilename, smallImageUrl, thumbUrl } from "./galleryImages";
import { useSpriteCell } from "./useSprites";
import { useIsMobile } from "./useIsMobile";
import CliqueAttribution from "../../features/aotd/components/shared/CliqueAttribution";

// Define spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Other styled-components omitted for brevity...

const LoadingContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
  position: absolute;
  inset: 0;
  /* border: 1px solid #ccc; // Add border to visualize the square */
`;

// The day's 40px sprite cell stretched over the card and blurred: shows the
// colours of the piece instantly while the thumbnail is still on its way.
const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  filter: blur(14px);
  transform: scale(1.08);
`;

const Spinner = styled.div`
  border: 16px solid #f3f3f3;
  border-radius: 5%;
  border-top: 16px solid #2e2e2e;
  width: 30px;
  height: 30px;
  animation: ${spin} 2s linear infinite;
`;

const ArtContainer = styled.div`
  width: auto;
  height: auto;
  /* Every AOTD is square: reserving the square up front stops the grid
     jumping as images arrive. */
  aspect-ratio: 1 / 1;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease-in-out;

  &:hover {
    /* transform: scale(1.07); */
  }
`;

const ArtImage = styled.img<{ $loaded: boolean }>`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.25s ease-out;
`;

const ArtTitle = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  /* background: rgba(0,0,0,0.5); */
  color: #fff;
  padding: 10px;
  font-weight: bold;
  font-size: 1.5rem;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const ArtAuthor = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 10px;
  right: 10px;
  /* background: rgba(0,0,0,0.5); */
  color: #fff;
  padding: 10px;
  font-weight: bold;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const ArtDate = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 10px;
  left: 10px;
  /* background: rgba(0,0,0,0.5); */
  color: #fff;
  padding: 10px;
  font-weight: bold;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "flex")};
  align-items: center;
  gap: 8px;
`;

const WaybackIconLink = styled.a<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
  width: 20px;
  height: 20px;
  opacity: 0.9;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(-1px 0 black) drop-shadow(0 1px black) drop-shadow(1px 0 black) drop-shadow(0 -1px black);
  }
`;

const ArtQuote = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* background: rgba(0,0,0,0.5); */
  color: #fff;
  padding: 10px;
  text-align: center;
  font-weight: bold;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const ArtEmojis = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 35px;
  left: 10px;
  color: #fff;
  padding: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  text-shadow: 
    -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black,
    2px 2px 2px white, -2px -2px 2px white, 2px -2px 2px white, -2px 2px 2px white;
  z-index: 2; // ensure it is above other elements if necessary
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const Art: React.FC<{ artInfo: ArtInfo }> = ({ artInfo }) => {
  const filename = artInfo?.filename;
  // thumb first; the old 1000px JPEG if the thumb route fails; nothing if both do
  const [source, setSource] = useState<{ filename?: string; step: 0 | 1 | 2 }>({ filename, step: 0 });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [touchCount, setTouchCount] = useState(0);
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);
  const placeholder = useSpriteCell(parseAotdFilename(filename));

  // The siteline can hand a mounted card a different day: start over for it.
  const step = source.filename === filename ? source.step : 0;
  if (source.filename !== filename) setSource({ filename, step: 0 });
  const loaded = !!filename && loadedFor === filename;
  const artUrl = !filename ? null : step === 0 ? thumbUrl(filename) : step === 1 ? smallImageUrl(filename) : null;

  const handleClick = () => {
    if (isMobile) {
      // toggle details on mobile
      setTouchCount((prev) => prev + 1);
    }
  };

  // Both sources failed: render nothing rather than an empty frame with
  // captions floating over it.
  if (!artInfo || !artUrl) return null;

  // console.log("Rendering Art component with emojis:", artInfo.emojis);

  return (
    <ArtContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {!loaded && (placeholder ? (
        <Placeholder style={placeholder} aria-hidden="true" />
      ) : (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ))}
      <ArtImage
        key={artUrl}
        src={artUrl}
        alt="Art of the day"
        loading="lazy"
        decoding="async"
        $loaded={loaded}
        onLoad={() => setLoadedFor(filename ?? null)}
        onError={() => setSource({ filename, step: step === 0 ? 1 : 2 })}
      />
      {loaded && (<>
      <ArtTitle isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}>
        {artInfo.title}
      </ArtTitle>
      <ArtAuthor isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}>
        by: <CliqueAttribution origin={artInfo.origin} fallback={artInfo.createdBy} />
      </ArtAuthor>
      <ArtDate isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}>
        <span>{artInfo.date.toLocaleDateString()}</span>
        {artInfo.waybackMachineUrl && (
          <WaybackIconLink
            href={artInfo.waybackMachineUrl}
            target="_blank"
            rel="noreferrer"
            isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}
            aria-label="View on Wayback Machine"
          >
            <svg
              viewBox="0 0 50.8 50.8"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M11.377 34.396V18.58m9.349 15.816V18.58m9.348 15.816V18.58m9.349 15.816V18.58m1.57-5.29H9.807m-.092 26.397h31.37" style={{ fill: "none", stroke: "#ffffff", strokeWidth: 3.175, strokeLinecap: "round", strokeLinejoin: "miter", strokeDasharray: "none", strokeOpacity: 1 }} />
              <path d="M160.724 172.373H31.276" style={{ fill: "none", stroke: "#ffffff", strokeWidth: 12, strokeLinecap: "round", strokeLinejoin: "miter", strokeDasharray: "none", strokeOpacity: 1 }} transform="translate(0 -.628) scale(.26458)" />
              <path d="M8.28 8.791h34.24L25.4 5.027Z" style={{ fill: "#ffffff", fillOpacity: 1, stroke: "#ffffff", strokeWidth: 1.5875, strokeLinecap: "round", strokeLinejoin: "round", strokeDasharray: "none", strokeOpacity: 1 }} />
            </svg>
          </WaybackIconLink>
        )}
      </ArtDate>
      {artInfo.emojis && artInfo.emojis.length > 0 && (
        <ArtEmojis isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}>
          {artInfo.emojis.join(" ")}
        </ArtEmojis>
      )}
      {artInfo.authorQuote && (
        <ArtQuote isVisible={(!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0)}>
          {artInfo.authorQuote}
        </ArtQuote>
      )}
      </>)}
    </ArtContainer>
  );
};

export default Art;
